/* eslint-disable max-lines */
import { pluralize } from "./word";

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_LONG_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];

const MONTH_NAMES = [
  "Jan.",
  "Feb.",
  "Mar.",
  "Apr.",
  "May",
  "June",
  "July",
  "Aug.",
  "Sep.",
  "Oct.",
  "Nov.",
  "Dec."
];
const MONTH_LONG_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

/**
 * Converts a human-readable delivery date string to ISO date format (YYYY-MM-DD)
 * @param {string} dateStr - e.g. "Today, Feb. 10", "Tomorrow, Feb. 11", "Thursday, Feb. 12"
 * @returns {string|null} ISO date string e.g. "2026-02-10", or null if parsing fails
 */
export const toISODate = dateStr => {
  if (!dateStr) return null;
  try {
    const datePart = dateStr.replace(/^[\w]+,\s*/, "");
    let monthIndex = MONTH_NAMES.findIndex(m => datePart.startsWith(m));
    let matchedName = MONTH_NAMES[monthIndex];

    // Fallback to long month names (backend uses full names for March–July)
    if (monthIndex === -1) {
      monthIndex = MONTH_LONG_NAMES.findIndex(m => datePart.startsWith(m));
      matchedName = MONTH_LONG_NAMES[monthIndex];
    }
    if (monthIndex === -1) return null;

    const day = parseInt(datePart.replace(matchedName, "").trim(), 10);
    if (Number.isNaN(day)) return null;

    const now = new Date();
    let year = now.getFullYear();
    if (now.getMonth() === 11 && monthIndex === 0) year += 1;

    const date = new Date(year, monthIndex, day);
    return formatFullDate(date);
  } catch {
    return null;
  }
};

export const formatFullDate = date => {
  const day = `0${date.getDate()}`.slice(-2);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

/**
 * Extracts the earliest delivery date from shipments as ISO date string (YYYY-MM-DD).
 * Falls back to parsing human-readable date strings when full_date is missing.
 * @param {Array} shipments - Array of shipment objects with full_date and/or date fields
 * @returns {string|null} ISO date string e.g. "2026-02-10", or null if no valid dates found
 */
export const getEarliestDeliveryDate = shipments => {
  if (!Array.isArray(shipments) || shipments.length === 0) return null;

  const parsedDates = shipments
    .map(s => s.full_date || toISODate(s.date))
    .filter(Boolean)
    .map(d => new Date(d).getTime())
    .filter(t => !Number.isNaN(t));

  if (parsedDates.length === 0) return null;

  return new Date(Math.min(...parsedDates)).toISOString().slice(0, 10);
};

export const formatShortDate = date => {
  const day = `0${date.getDate()}`.slice(-2);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatWithWday = date => {
  const day = `0${date.getDate()}`.slice(-2);
  const month = MONTH_LONG_NAMES[date.getMonth()];
  const weekday = WEEKDAY_NAMES[date.getDay()];
  return `${weekday}, ${month} ${day}`;
};

export const formatWithFullWday = (date, months = MONTH_NAMES) => {
  const day = `${date.getDate()}`.slice(-2);
  const month = months[date.getMonth()];
  const weekday = WEEKDAY_LONG_NAMES[date.getDay()];
  return `${weekday}, ${month} ${day}`;
};

export const formatWithWdayLong = date => {
  const day = `0${date.getDate()}`.slice(-2);
  const month = MONTH_LONG_NAMES[date.getMonth()];
  const year = date.getFullYear();
  const weekday = WEEKDAY_LONG_NAMES[date.getDay()];
  return `${weekday}, ${month} ${day} ${year}`;
};

export const formatHoursMinutes = time => {
  const hours = Math.floor(time);
  let minutes = Number(60 * (time % 1)).toFixed();
  minutes = minutes > 0 ? minutes : 1;
  const hoursWord = pluralize(hours, "hour");
  const minutesWord = pluralize(minutes, "minute");
  return hours > 0 ? `${hoursWord} ${minutesWord}` : minutesWord;
};

/**
 * Format time to AM/PM format (e.g., "7AM", "12PM")
 * Uses timezone from Rails configuration for consistent display
 * @param {Date} date - The date to format
 * @param {boolean} ceilHour - If true (default), rounds up hour when minutes > 0
 * @returns {string} Formatted time string (e.g., "7AM", "12PM")
 */
function formatTo12Hour(hours) {
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}${ampm}`;
}

function formatDateToTime(date, ceilHour) {
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const shouldCeilHour = minutes => minutes > 0 && ceilHour;
  const incrementHour = hours => {
    let newHours = hours + 1;
    if (newHours >= 24) newHours = 0;
    return newHours;
  };

  let hours = date.getHours();
  const minutes = date.getMinutes();

  if (shouldCeilHour(minutes)) {
    hours = incrementHour(hours);
  }

  return formatTo12Hour(hours);
}

/**
 * Convert a date-time string or Date object to a 12-hour label (e.g., "3AM").
 * Rounds up one hour if minutes > 0 when `ceilHour` is true; wraps at midnight.
 * @param {string|Date} dateString
 * @param {boolean} [ceilHour=true] default to true
 * @returns {string}
 */
export function formatTimeAMPM(dateString, ceilHour = true) {
  // Handle Date objects directly to preserve local timezone
  if (dateString instanceof Date) {
    return formatDateToTime(dateString, ceilHour);
  }

  // Handle null, undefined, and non-string inputs
  if (dateString == null || typeof dateString !== "string") {
    return "";
  }

  // Extract time components directly from the string - support 1 or 2 digit hours/minutes
  const timeMatch = dateString.match(/T(\d{1,2}):(\d{1,2})/);
  const shouldCeilHour = minutes => minutes > 0 && ceilHour;
  const incrementHour = hours => {
    let newHours = hours + 1;
    if (newHours >= 24) newHours = 0;
    return newHours;
  };

  if (!timeMatch) {
    // Fallback to Date parsing if format is unexpected
    return formatDateToTime(new Date(dateString), ceilHour);
  }

  // Parse hours and minutes from the matched time
  let hours = parseInt(timeMatch[1], 10);
  const minutes = parseInt(timeMatch[2], 10);

  // If minutes > 1, bump the hour
  if (shouldCeilHour(minutes)) {
    hours = incrementHour(hours);
  }

  return formatTo12Hour(hours);
}

/**
 * Format delivery time range from ISO datetime strings to human readable format
 * @param {string} deliveryTime - ISO datetime string for start time
 * @param {string} deliveryTimeEnd - ISO datetime string for end time
 * @returns {string} Formatted time range (e.g., "7AM – 12PM")
 */
export const formatDeliveryTimeRange = (deliveryTime, deliveryTimeEnd) => {
  if (!deliveryTime || !deliveryTimeEnd) {
    return "";
  }

  return `${formatTimeAMPM(deliveryTime)} – ${formatTimeAMPM(deliveryTimeEnd)}`;
};

/**
 * Extract day part from delivery date string
 * @param {string} date - Date string (e.g., "Today, Aug. 30")
 * @returns {string} Day part (e.g., "Today")
 */
export const extractDayFromDate = date => {
  if (!date) return "";

  if (date.includes(",")) return date.split(",")[0].trim();

  return date;
};

/**
 * Check if date string contains today or tomorrow
 * @param {string} date - Date string to check
 * @returns {boolean} True if date contains "today" or "tomorrow"
 */
export const shouldShowTimeRange = date => {
  if (!date) return false;
  const lowerDate = date.toLowerCase();
  return lowerDate.includes("today") || lowerDate.includes("tomorrow");
};

/**
 * Shorten weekday names to abbreviations in a date string
 * @param {string} dateString - Date string (e.g., "Monday, Aug. 30")
 * @returns {string} Date string with shortened weekday (e.g., "Mon. Aug. 30")
 */
export const shortenWeekday = dateString => {
  if (!dateString) return dateString;

  const lowerDate = dateString.toLowerCase();
  if (lowerDate.startsWith("today") || lowerDate.startsWith("tomorrow")) {
    return dateString;
  }

  for (let i = 0; i < WEEKDAY_LONG_NAMES.length; i += 1) {
    if (dateString.startsWith(WEEKDAY_LONG_NAMES[i])) {
      return dateString.replace(`${WEEKDAY_LONG_NAMES[i]}, `, `${WEEKDAY_NAMES[i]}. `);
    }
  }

  return dateString;
};
